import "../plan.scss"

import { LegacyCard, ProgressBar, Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import { useMemo } from "react"

import { FormatPriceText } from "@/components"
import { PLAN_LIST_MAP } from "@/constant"
import stores from "@/stores"
import dayjs from "@/utils/dayjs"
import { formatDisplayNumber } from "@/utils/formatNumber"

const AccountInfo = () => {
  const { PlanStore, UserInfoStore } = stores || {}
  const { completedCurPlan, config } = PlanStore

  const {
    plan_total_quota = 0,
    back_in_store_value,
    plan_used_quota = 0,
    cur_period_end,
    extra_charge_count,
    package_period,
    package_kind = 1,
    total_ins_price,
  } = config?.user_pkg || {}
  const { origin_price } = completedCurPlan || {}
  const periodType = package_period === 1 ? t("Monthly") : t("Annually")
  const packageName = PLAN_LIST_MAP()[package_kind].planText

  const planText = useMemo(() => {
    if (package_kind === 1) {
      return packageName
    }
    if (PlanStore.config?.user_pkg.source === 3) {
      return (
        <>
          {`ParcelWILL Bundle - ${periodType} - `}
          <FormatPriceText
            value={PlanStore.config.user_pkg.shopify_origin_price}
            currency="$"
          />
        </>
      )
    }
    return (
      <>
        {`${packageName} - ${periodType} - `}
        <FormatPriceText value={origin_price} currency="$" />
      </>
    )
  }, [
    PlanStore.config?.user_pkg.shopify_origin_price,
    PlanStore.config?.user_pkg.source,
    origin_price,
    packageName,
    package_kind,
    periodType,
  ])

  const nextPlanText = useMemo(() => {
    if (!PlanStore.config?.delay?.package_id) return ""
    // 只有买了bundle套餐才显示
    if (PlanStore.config.delay.source !== 3) return ""
    // delay.package_id 为 0 表示没有nextplan
    if (PlanStore.config.delay.package_id < 1) return ""
    if (PlanStore.config.delay.package_id === 1) {
      return `Free`
    }

    return (
      <>
        {`ParcelWILL Bundle - ${PlanStore.config?.delay?.period === 1 ? t("Monthly") : t("Annually")} - `}
        <FormatPriceText
          value={PlanStore.config?.delay?.shopify_origin_price}
          currency="$"
        />
      </>
    )
  }, [PlanStore.config])

  return (
    <LegacyCard>
      <LegacyCard.Section>
        <div className="flex flex-col gap-4">
          <Text variant="headingMd" as="span">
            {t("PlanInfo")}
          </Text>
        </div>
      </LegacyCard.Section>
      <LegacyCard.Section>
        <div className="flex flex-wrap gap-x-12 gap-y-4">
          <div className="flex flex-col gap-1">
            <Text variant="headingSm" as="span">
              {t("CurrentPlan")}
            </Text>
            <Text variant="bodyMd" as="span" tone="subdued">
              {planText}
            </Text>
          </div>
          {!!nextPlanText && (
            <div className="flex flex-col gap-1">
              <Text variant="headingSm" as="span">
                {"Next Plan"}
              </Text>
              <Text variant="bodyMd" as="span" tone="subdued">
                {nextPlanText}
              </Text>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Text variant="headingSm" as="span">
              {t("NextQuotaResetDate")}
            </Text>
            <Text variant="bodyMd" as="span" tone="subdued">
              {cur_period_end
                ? dayjs.unix(cur_period_end ?? 0).format("DD MMM YYYY")
                : t("Free")}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="headingSm" as="span">
              {t("FreeReturnServiceSales")}
            </Text>
            <Text variant="bodyMd" as="span" tone="subdued">
              <FormatPriceText
                value={total_ins_price ?? 0}
                currency={UserInfoStore.data.currency_format}
              />
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="headingSm" as="span">
              {t("BackInStoreValue")}
            </Text>
            <Text variant="bodyMd" as="span" tone="subdued">
              <FormatPriceText
                value={back_in_store_value ?? 0}
                currency={UserInfoStore.data.currency_format}
              />
            </Text>
          </div>
        </div>
      </LegacyCard.Section>
      <LegacyCard.Section>
        <div style={{ borderTop: "1px solid #E1E2E3" }}>
          <div className="my-4">
            <Text variant="headingSm" as="span">
              {t("PeriodTypeReturnsQuota", {
                replace: {
                  periodType,
                },
              })}
            </Text>
          </div>
          <div className="mb-1">
            <Text variant="bodyMd" as="span">
              {t("OfPlanTotalQuotaUsed", {
                replace: {
                  useQuota: formatDisplayNumber(plan_used_quota ?? 0),
                  totalQuota: formatDisplayNumber(plan_total_quota ?? 0),
                },
              })}
            </Text>
          </div>
          <div className="plan-progressbar-height">
            <ProgressBar
              progress={(100 / plan_total_quota) * plan_used_quota}
              size="small"
              tone="primary"
            />
          </div>
          {package_period !== 2 && (
            <div className="mt-1">
              <Text variant="bodyMd" as="span" tone="subdued">
                {t("AdditionalQuotaUsed")}:{" "}
              </Text>
              <Text variant="bodyMd" as="span" fontWeight="medium">
                {extra_charge_count}
              </Text>
            </div>
          )}
        </div>
      </LegacyCard.Section>
    </LegacyCard>
  )
}

export default observer(AccountInfo)
